/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { configurationApi } from '../lib/api';
import { setRuntimeReferentials } from '../lib/utils';
import { useAuth } from './AuthContext';

const ConfigContext = createContext(null);

export function ConfigProvider({ children }) {
  const { user } = useAuth();
  const [config, setConfig] = useState({ menus: [], referential_types: [], referentials: [] });
  const [loaded, setLoaded] = useState(false);

  const refreshConfig = async () => {
    try {
      const res = await configurationApi.get();
      const next = res.data || { menus: [], referential_types: [], referentials: [] };
      setConfig(next);
      setRuntimeReferentials(next.referentials || []);
    } catch (_) {
      // Public/login screens can exist before authentication; keep fallbacks.
    } finally { setLoaded(true); }
  };

  useEffect(() => { if (user) refreshConfig(); }, [user?.id]);

  const value = useMemo(() => ({
    ...config,
    loaded,
    refreshConfig,
    getList: (type, fallback = []) => {
      const rows = (config.referentials || [])
        .filter(r => r.type === type && r.active !== false)
        .sort((a,b) => Number(a.order||0)-Number(b.order||0) || String(a.label||'').localeCompare(String(b.label||'')))
        .map(r => ({ value:r.code, label:r.label, color:r.color, ...r }));
      return rows.length ? rows : fallback;
    },
    getLabel: (type, code, fallback) => {
      const row = (config.referentials || []).find(r => r.type === type && r.code === code && r.active !== false);
      return row?.label || fallback || code;
    }
  }), [config, loaded]);

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export function useAppConfig() {
  return useContext(ConfigContext) || { menus:[], referential_types:[], referentials:[], loaded:false, refreshConfig:async()=>{}, getList:(_t,f=[])=>f, getLabel:(_t,c,f)=>f||c };
}
